import { useState, useMemo } from 'react';

import { FILTER_TYPES } from '../constants';
import type {
  ProcessedCourseWiseBatchesData,
  FilteredCourseWiseBatchesData,
  ProcessedBatch,
} from '../types';

interface UseBatchFilteringProps {
  processedBatchesData: ProcessedCourseWiseBatchesData;
}

interface UseBatchFilteringReturn {
  selectedFilter: string;
  selectedDays: string[];
  filteredBatchesData: FilteredCourseWiseBatchesData;
  setSelectedFilter: (filter: string) => void;
  handleDayFilterApply: (days: string[]) => void;
  handleDayFilterClear: () => void;
}

export const useBatchFiltering = ({
  processedBatchesData,
}: UseBatchFilteringProps): UseBatchFilteringReturn => {
  const [selectedFilter, setSelectedFilter] = useState<string>(
    FILTER_TYPES.RECOMMENDED
  );
  const [selectedDays, setSelectedDays] = useState<string[]>([]);

  const filteredBatchesData = useMemo((): FilteredCourseWiseBatchesData => {
    const filteredData: FilteredCourseWiseBatchesData = {};

    // Helper function to check if a batch matches the selected days
    const matchesSelectedDays = (batch: ProcessedBatch): boolean => {
      if (selectedDays.length === 0) return true; // No day filter applied

      const batchDays = new Set(
        batch.scheduleDetails.map(schedule => schedule.day)
      );
      return selectedDays.every(selectedDay => batchDays.has(selectedDay));
    };

    for (const courseName of Object.keys(processedBatchesData)) {
      const courseData = processedBatchesData[courseName];

      let filteredBatches: ProcessedBatch[] = [];
      let otherBatches: ProcessedBatch[] = [];

      switch (selectedFilter) {
        case FILTER_TYPES.RECOMMENDED: {
          filteredBatches = courseData.recommended_batches;
          otherBatches = courseData.other_batches;
          break;
        }

        case FILTER_TYPES.STARTING_SOON:
        case FILTER_TYPES.NEW_BATCHES:
        case FILTER_TYPES.FAST_FILLING: {
          const allBatches: ProcessedBatch[] = [
            ...courseData.recommended_batches,
            ...courseData.other_batches,
          ];
          filteredBatches = allBatches.filter(
            (batch: ProcessedBatch) =>
              Array.isArray(batch.tags) && batch.tags.includes(selectedFilter)
          );
          otherBatches = allBatches.filter(
            (batch: ProcessedBatch) =>
              !Array.isArray(batch.tags) || !batch.tags.includes(selectedFilter)
          );
          break;
        }

        default: {
          break;
        }
      }

      // Apply day filtering only to filtered batches (restrictive - can only move from filtered to other)
      /**
       * TECH-DEBT: Namespacing for filteredBatches is not right
       *  - Currently, when selectedDays is applied, filteredBatches is empty
       *  - This is because the day matching batches are added to otherBatches
       *  - Ideally, we will introduce a new namespace for the day matching batches
       *  - And implement this in the batches-list component
       */
      if (selectedDays.length > 0) {
        const dayMatchingBatches = [
          ...courseData.recommended_batches,
          ...courseData.other_batches,
        ].filter(batch => matchesSelectedDays(batch));

        filteredBatches = [];
        otherBatches = [...dayMatchingBatches];
      }

      filteredData[courseName] = {
        filtered_batches: filteredBatches,
        other_batches: otherBatches,
      };
    }

    return filteredData;
  }, [processedBatchesData, selectedFilter, selectedDays]);

  const handleDayFilterApply = (days: string[]) => {
    setSelectedDays(days);
  };

  const handleDayFilterClear = () => {
    setSelectedDays([]);
  };

  return {
    selectedFilter,
    selectedDays,
    filteredBatchesData,
    setSelectedFilter,
    handleDayFilterApply,
    handleDayFilterClear,
  };
};
